/**
 * Segnalazioni — client REST verso collaboration-service `/issues/*`.
 *
 * Base URL: pattern esistente frontend-shared (`COLLAB_API_BASE` =
 * `VITE_COLLABORATION_URL` oppure proxy dev `/api-collab`).
 * Endpoint senza auth (pairing + creazione studente): `collabFetch` (plain fetch).
 * Endpoint autenticati (identify, list, detail, patch, creazione teacher): `authFetch`.
 *
 * Regola house: nessun errore silenziato — ogni fetch fallito lancia Error
 * con status e dettaglio; i caller loggano con console.warn/error.
 */
import { authFetch, collabFetch, COLLAB_API_BASE } from '@tl4k/frontend-shared';
import type {
  IssueCreateResponse,
  IssueListResponse,
  IssuePairing,
  IssueStatus,
  IssueUpdatePayload,
  PairingIdentifyResponse,
  PairingStatusResponse,
  Tl4kIssue,
} from './types';

/** Base URL collaboration-service senza slash finale. */
function issuesBase(): string {
  return `${COLLAB_API_BASE.replace(/\/+$/, '')}/issues`;
}

/** Errore strutturato con status HTTP e messaggio del server quando disponibile. */
export class IssuesApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'IssuesApiError';
    this.status = status;
  }
}

async function parseError(res: Response, fallback: string): Promise<IssuesApiError> {
  let detail = '';
  try {
    const data = (await res.json()) as { error?: unknown; detail?: unknown; message?: unknown };
    detail = String(data?.error ?? data?.detail ?? data?.message ?? '');
  } catch {
    // corpo non JSON: usa solo il messaggio di fallback (log per no-silent-errors)
    console.warn(`[issuesApi] risposta non JSON da ${res.url} (HTTP ${res.status})`);
  }
  return new IssuesApiError(res.status, detail || fallback);
}

const JSON_HEADERS = { 'Content-Type': 'application/json' } as const;

// ────────────────────────────────────────────────────────────────
// Pairing (lato studente, nessuna auth)
// ────────────────────────────────────────────────────────────────

export interface CreatePairingPayload {
  app?: string;
  mode?: string;
  url?: string;
  user_agent?: string;
}

/** POST /issues/pairing — crea pairing (token + short_code), nessuna auth. */
export async function createPairing(payload: CreatePairingPayload = {}): Promise<IssuePairing> {
  const res = await collabFetch(`${issuesBase()}/pairing`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Creazione pairing fallita');
  return (await res.json()) as IssuePairing;
}

/** POST /issues/pairing/status — polling fallback (nessuna auth). */
export async function getPairingStatus(token: string): Promise<PairingStatusResponse> {
  const res = await collabFetch(`${issuesBase()}/pairing/status`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw await parseError(res, 'Lettura stato pairing fallita');
  return (await res.json()) as PairingStatusResponse;
}

/**
 * POST /issues/pairing/identify — identifica il client (JWT teacher-or-above).
 * Passare `token` (da QR) oppure `shortCode` (input manuale).
 */
export async function identifyPairing(
  input: { token?: string; shortCode?: string },
): Promise<PairingIdentifyResponse> {
  const body = input.token ? { token: input.token } : { short_code: input.shortCode };
  const res = await authFetch(`${issuesBase()}/pairing/identify`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res, 'Identificazione client fallita');
  return (await res.json()) as PairingIdentifyResponse;
}

// ────────────────────────────────────────────────────────────────
// Creazione issue
// ────────────────────────────────────────────────────────────────

/**
 * POST /issues — Path A (studente): nessuna auth, pairing_token identificato.
 */
export async function createStudentIssue(payload: {
  pairing_token: string;
  description?: string;
  context?: Record<string, unknown>;
}): Promise<IssueCreateResponse> {
  const res = await collabFetch(issuesBase(), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Invio segnalazione fallito');
  return (await res.json()) as IssueCreateResponse;
}

/**
 * POST /issues — Path B (insegnante diretto): JWT teacher-or-above, senza pairing.
 */
export async function createTeacherIssue(payload: {
  description?: string;
  context?: Record<string, unknown>;
  session_pin?: string;
}): Promise<IssueCreateResponse> {
  const res = await authFetch(issuesBase(), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Invio segnalazione fallito');
  return (await res.json()) as IssueCreateResponse;
}

// ────────────────────────────────────────────────────────────────
// Lettura / aggiornamento (auth)
// ────────────────────────────────────────────────────────────────

export interface ListIssuesParams {
  status?: IssueStatus;
  app?: string;
  limit?: number;
}

/** GET /issues — lista (reporter o tutte se superadmin). */
export async function listIssues(params: ListIssuesParams = {}): Promise<IssueListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.app) qs.set('app', params.app);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authFetch(`${issuesBase()}${suffix}`);
  if (!res.ok) throw await parseError(res, 'Lettura segnalazioni fallita');
  return (await res.json()) as IssueListResponse;
}

/**
 * Il server risponde a GET e PATCH /issues/:id con una busta `{ issue: {...} }`.
 * Trattare il corpo come la segnalazione stessa (com'era qui) riempiva il
 * dettaglio di `undefined` dopo "Salva modifiche" e mandava la richiesta delle
 * foto a /issues/undefined/attachments. Una busta mancante è un errore
 * esplicito, non una segnalazione vuota.
 */
async function readIssueEnvelope<T>(res: Response, azione: string): Promise<T> {
  let body: { issue?: unknown } | null = null;
  try {
    body = (await res.json()) as { issue?: unknown } | null;
  } catch (e) {
    console.warn(`[issuesApi] ${azione}: risposta non JSON (HTTP ${res.status})`, e);
  }
  const issue = body?.issue;
  if (!issue || typeof issue !== 'object') {
    throw new IssuesApiError(res.status, `Risposta del server non valida (${azione})`);
  }
  return issue as T;
}

/** GET /issues/:id — dettaglio (solo reporter o superadmin). */
export async function getIssue(id: number): Promise<Tl4kIssue> {
  const res = await authFetch(`${issuesBase()}/${encodeURIComponent(String(id))}`);
  if (!res.ok) throw await parseError(res, 'Lettura dettaglio segnalazione fallita');
  return readIssueEnvelope<Tl4kIssue>(res, 'lettura dettaglio');
}

// ────────────────────────────────────────────────────────────────
// Foto allegate (auth, reporter o superadmin)
// ────────────────────────────────────────────────────────────────

export interface IssueAttachment {
  id: number;
  mime: string;
  size_bytes: number;
  created_at: string;
}

function attachmentsUrl(issueId: number, attachmentId?: number): string {
  const base = `${issuesBase()}/${encodeURIComponent(String(issueId))}/attachments`;
  return attachmentId === undefined ? base : `${base}/${encodeURIComponent(String(attachmentId))}`;
}

/** GET /issues/:id/attachments — metadati delle foto (senza byte). */
export async function listAttachments(issueId: number): Promise<{ attachments: IssueAttachment[]; max: number }> {
  const res = await authFetch(attachmentsUrl(issueId));
  if (!res.ok) throw await parseError(res, 'Lettura foto fallita');
  return (await res.json()) as { attachments: IssueAttachment[]; max: number };
}

/** POST /issues/:id/attachments — carica una foto (multipart, campo `file`). */
export async function uploadAttachment(issueId: number, file: Blob, filename: string): Promise<IssueAttachment> {
  const form = new FormData();
  form.append('file', file, filename);
  // Niente Content-Type manuale: il browser imposta il boundary multipart.
  const res = await authFetch(attachmentsUrl(issueId), { method: 'POST', body: form });
  if (!res.ok) throw await parseError(res, 'Caricamento foto fallito');
  return ((await res.json()) as { attachment: IssueAttachment }).attachment;
}

/** GET /issues/:id/attachments/:attId — byte della foto (serve il JWT: niente <img src> diretto). */
export async function fetchAttachmentBlob(issueId: number, attachmentId: number): Promise<Blob> {
  const res = await authFetch(attachmentsUrl(issueId, attachmentId));
  if (!res.ok) throw await parseError(res, 'Lettura foto fallita');
  return await res.blob();
}

/** DELETE /issues/:id/attachments/:attId */
export async function deleteAttachment(issueId: number, attachmentId: number): Promise<void> {
  const res = await authFetch(attachmentsUrl(issueId, attachmentId), { method: 'DELETE' });
  if (!res.ok) throw await parseError(res, 'Eliminazione foto fallita');
}

/** Colonne che il PATCH restituisce davvero: il resto resta quello già in mano. */
export type IssueUpdateResult = Pick<Tl4kIssue, 'id' | 'short_id' | 'status'> & Partial<Tl4kIssue>;

/** PATCH /issues/:id — cambio stato + nota di risoluzione. Risposta parziale. */
export async function updateIssue(id: number, payload: IssueUpdatePayload): Promise<IssueUpdateResult> {
  const res = await authFetch(`${issuesBase()}/${encodeURIComponent(String(id))}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res, 'Aggiornamento segnalazione fallito');
  return readIssueEnvelope<IssueUpdateResult>(res, 'aggiornamento');
}

/** Applica la risposta parziale del PATCH sulla segnalazione già caricata. */
export function mergeIssueUpdate(current: Tl4kIssue, patch: IssueUpdateResult): Tl4kIssue {
  return { ...current, ...patch };
}
